import { toast } from 'sonner';
import axios from 'axios';

const BATCH_SIZE = 500;

// Upload in batches so large files never hit the proxy's ~100s request time limit
export const uploadPricelistInBatches = async (apiBase, hospitalName, items, axiosConfig, onProgress) => {
  let created = 0, skipped = 0;
  const totalBatches = Math.ceil(items.length / BATCH_SIZE);
  for (let b = 0; b < totalBatches; b++) {
    const batch = items.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
    if (onProgress) onProgress(b + 1, totalBatches);
    const res = await axios.post(`${apiBase}/admin/pricelists/bulk`, { hospital_name: hospitalName, items: batch }, axiosConfig);
    created += (res.data.created || []).length;
    skipped += (res.data.skipped || []).length;
  }
  return { created, skipped };
};

export const parseCsvPriceList = (csvText) => {
  const lines = csvText.trim().split('\n');
  const items = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const parts = line.split(',').map((p) => p.trim());
    if (parts.length >= 4) {
      // New format: ID, NAME, TYPE, UOM, COST — old format (no UOM): ID, NAME, TYPE, COST
      const isOldFormat = parts.length === 4 || (parts[3] !== '' && !isNaN(parseFloat(parts[3].replace(/[^0-9.-]/g, ''))) && isNaN(parseFloat((parts[4] || '').replace(/[^0-9.-]/g, ''))));
      const uom = isOldFormat ? '' : parts[3];
      const cost = (isOldFormat ? parts[3] : parts[4] || '').replace(/[^0-9.-]/g, '');
      items.push({ item_id: parts[0], item_name: parts[1], item_type: parts[2], cost, uom });
    }
  }
  return items;
};

// Expected columns: FIRST NAME, MIDDLE NAME, LAST NAME, D.O.B, SEX, RELATIONSHIP, SERIAL NUMBER, FAMILY ID, ALLOTMENT
export const parseCsvFamilies = (csvText) => {
  const lines = csvText.trim().split('\n');
  const familiesMap = {};
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const parts = line.split(/\t|,/).map((p) => p.trim());
    if (parts.length >= 9) {
      const familyId = parts[7];
      const member = {
        first_name: parts[0],
        middle_name: parts[1],
        last_name: parts[2],
        dob: parts[3],
        sex: parts[4],
        // Accept both "Principle" and "Principal" (any case); normalize to "Principle"
        relationship: parts[5] && parts[5].trim().toLowerCase() === 'principal' ? 'Principle' : parts[5],
        serial_number: parts[6],
      };

      if (!familiesMap[familyId]) {
        const principleMember = member.relationship === 'Principle'
          ? `${parts[0]} ${parts[1]} ${parts[2]}`.trim() : '';
        familiesMap[familyId] = {
          family_id: familyId,
          principle_member_name: principleMember,
          total_allotment: parseFloat(parts[8]),
          remaining_balance: parseFloat(parts[8]),
          members: [],
        };
      }

      if (member.relationship === 'Principle') {
        familiesMap[familyId].principle_member_name = `${parts[0]} ${parts[1]} ${parts[2]}`.trim();
      }

      familiesMap[familyId].members.push(member);
    }
  }
  return Object.values(familiesMap);
};

const downloadCsv = (csvContent, filename, successMessage) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
  toast.success(successMessage);
};

export const downloadPricelistTemplate = () => downloadCsv(
  `ITEM ID,ITEM NAME,ITEM TYPE,UoM,COST
SERV-001,General Consultation,Service,Visit,50
SERV-002,X-Ray Scan,Service,Scan,150
SERV-003,Blood Test,Service,Test,30
DRUG-001,Paracetamol 500mg,Drug,Tablet,2
DRUG-002,Amoxicillin 250mg,Drug,Capsule,5
DRUG-003,Ibuprofen 400mg,Drug,,3`,
  'pricelist_template.csv',
  'Price list template downloaded! Open with Excel to fill in your data.'
);

export const downloadFamilyTemplate = () => downloadCsv(
  `FIRST NAME,MIDDLE NAME,LAST NAME,D.O.B,SEX,RELATIONSHIP,SERIAL NUMBER,FAMILY ID,ALLOTMENT
Tong,Wol,Akoc,1/1/1965,Female,Principle,SEC-2413-00,SEC-2413,2000
Mary,William,Wichild,1/1/1985,Female,Spouse,SEC-2413-01,SEC-2413,2000
Daivd,Tong,Wol,1/1/2003,Male,Child,SEC-2413-02,SEC-2413,2000
Arwell,Tong,Wol,1/1/2005,Female,Child,SEC-2413-03,SEC-2413,2000
Akouch,Tong,Wol,1/1/2007,Male,Child,SEC-2413-04,SEC-2413,2000
Wol,Tong,Wol,1/1/2010,Male,Child,SEC-2413-05,SEC-2413,2000
Ajok,Tong,Wol,1/1/2011,Female,Child,SEC-2413-06,SEC-2413,2000
Adhieu,Tong,Wol,1/1/2021,Female,Child,SEC-2413-07,SEC-2413,2000`,
  'family_bulk_upload_template.csv',
  'Family template downloaded! Open with Excel to fill in your data.'
);
